// @ts-ignore
import type {
  CustomValidationCheckOptions,
  ValidationCheck,
  ValidationCheckResults,
  ValidationRules,
  ValidationState,
  ValidationSummary,
} from "@/composables/questions/types";
import {default as Validator} from "fastest-validator";
import {getFreeTextAnswer} from "pg-isomorphic/answers/values";
import type {Answers} from "pg-isomorphic/api/connection";
import type {GroupType} from "pg-isomorphic/enums";
import {Internal, QuestionType} from "pg-isomorphic/enums";
import {ValidationType} from "pg-isomorphic/enums/question";
import {filter, findGroup, findGroupInstance, isLocaleObjectQuestion} from "pg-isomorphic/profile";
import type {JSONQuestion, JSONValue} from "pg-isomorphic/utils";
import {advancedIsUnanswered} from "pg-isomorphic/utils";
import type {Dictionary} from "ramda";
import {any, is, isEmpty, isNil, path, pathOr, prop, values} from "ramda";
// @ts-ignore
const zxcvbn = () => import("zxcvbn");
const valExpExceptionTypes: (QuestionType | GroupType)[] = [QuestionType.ROUTING];

let zxcvbnResponse: any = null;

/**
 * Used to get the validation translation messages
 *
 * @param validatorNames
 */
function addTranslation(validatorNames: string[]) {
  let messages: {[key: string]: string} = {};
  for (const name of validatorNames) {
    messages[name] = `validation.${name}`; // just get the i18n key
  }
  return messages;
}

export async function getZxcvbnLibrary() {
  zxcvbnResponse = await zxcvbn();
  return true;
}

export async function getZxcvbnScore(val) {
  if (!zxcvbnResponse) {
    await getZxcvbnLibrary();
  }
  return zxcvbnResponse.default(val);
}

/**
 * Gets a new validation instance, complete with our own custom validation checks
 *
 * @param rules
 */
export function getValidationInstance(rules: ValidationRules) {
  let messages = addTranslation([
    "required",
    "string",
    "stringEmpty",
    "stringMin",
    "stringMax",
    "stringLength",
    "stringPattern",
    "stringContains",
    "stringEnum",
    "stringAlpha",
    "stringMatch",
    "number",
    "numberMin",
    "numberMax",
    "numberEqual",
    "numberNotEqual",
    "numberInteger",
    "numberPositive",
    "numberNegative",
    "array",
    "arrayEmpty",
    "arrayMin",
    "arrayMax",
    "arrayLength",
    "arrayContains",
    "arrayEnum",
    "boolean",
    "function",
    "date",
    "dateMin",
    "dateMax",
    "forbidden",
    "email",
    "shortUrl",
    "strongPassword",
    "groupKey",
    "ein",
    "com-uk",
    "com-be",
    "graphiteEmail",
    "routeNotValid",
    "ibanNotValid",
    "answerRequiredToSave",
  ]);
  let v = new Validator({
    messages,
  });
  // eslint-disable-next-line no-unused-vars
  v.add(
    "shortUrl",
    (value: string) => {
      if (!/^(?:http(s)?:\/\/)?[\w.-]+(?:\.[\w.-]+)+[\w\-._~:/?#[\]@!$&'()*+,;=.]+$/gm.test(value)) {
        return v.makeError("shortUrl", null, value);
      }
      return true;
    } /* function (this: Validator, {messages}) {
    return {
      source: `
        if (!(/^(?:http(s)?:\\/\\/)?[\\w.-]+(?:\\.[\\w.-]+)+[\\w\\-._~:/?#[\\]@!$&'()*+,;=.]+$/gm).test(value)) {
          return ${this.makeError({type: "shortUrl", actual: "value", messages})};
        }

        return value;
      `,
    };
  }*/,
  );

  if (path(["type"], rules) === "strongPassword") {
    if (zxcvbnResponse) {
      v.add(
        "strongPassword",
        (value: string) => {
          const zxcvbn = zxcvbnResponse.default;
          if (value && value.length >= rules.passMin) {
            const result = zxcvbn(value);
            if (result.score >= rules.passScore) {
              return true;
            }
          }
          return v.makeError("strongPassword", null, value);
        } /*function (this: Validator, {messages}, path, context) {
        // @ts-ignore
        context.zxcvbn = zxcvbnResponse.default; // HACK, consider alternative validation lib
        return {
          source: `
            if (value && value.length >= ${rules.passMin}) {
              const result = context.zxcvbn(value);
              if (result.score >= ${rules.passScore}) {
                return true;
              }
            }
            return ${this.makeError({type: "strongPassword", actual: "value", messages})};
          `,
        };}*/,
      );
    }
  }
  v.add(
    "stringMatch",
    (value: string) => {
      const matched = typeof rules.matchValue === "function" ? rules.matchValue() : rules.matchValue;
      if (value && value === matched) {
        return true;
      }
      return v.makeError("stringMatch", null, value);
    } /*function (this: Validator, {messages}) {
    return {
      source: `
        if (value && value === '${typeof rules.matchValue === "function" ? rules.matchValue() : rules.matchValue}') {
          return true;
        }
        return ${this.makeError({type: "stringMatch", actual: "value", messages})};
      `,
    };
  }*/,
  );
  v.add(
    "ein",
    (value: string) => {
      const replacedValue = value.replace("-", "");

      if (replacedValue.length !== 9) {
        return v.makeError("ein", null, value);
      }

      // Repeating characters are not valid (999999999)
      const firstCharacter = replacedValue.substring(0, 1);
      let re = new RegExp(`${firstCharacter}{9}`);
      if (re.test(replacedValue)) {
        return v.makeError("ein", null, value);
      }

      // Sequences are not allowed (123456789)
      if (replacedValue === "123456789" || replacedValue === "987654321") {
        return v.makeError("ein", null, value);
      }

      return true;
    },
    /* function (this: Validator, {messages}) {
    return {
      source: `
        const replacedValue = value.replace('-', '')

        if (replacedValue.length !== 9) {
          return ${this.makeError({type: "ein", actual: "value", messages})};
        }

        // Repeating characters are not valid (999999999)
        const firstCharacter = replacedValue.substring(0, 1);
        let re = new RegExp(\`\${firstCharacter}{9}\`);
        if (re.test(replacedValue)) {
          return ${this.makeError({type: "ein", actual: "value", messages})};
        }

        // Sequences are not allowed (123456789)
        if (replacedValue === '123456789' || replacedValue === '987654321') {
          return ${this.makeError({type: "ein", actual: "value", messages})};
        }

        return true;
      `,
    };
  }*/
  );
  v.add(
    "com-uk",
    (value: string) => {
      const replacedValue = value.split("GB").join("");
      let error = true;
      if (
        (!isNaN(replacedValue as any) && (replacedValue.length === 9 || replacedValue.length === 12)) ||
        replacedValue.length === 5
      ) {
        error = false;
      }

      if (error) {
        return v.makeError("com-uk", null, value);
      }

      return true;
    } /*function (this: Validator, {messages}) {
    return {
      source: `
        const replacedValue = value.split('GB').join('');
        let error = true;
        if (
          (!isNaN(replacedValue) && (replacedValue.length === 9 || replacedValue.length === 12)) ||
          replacedValue.length === 5
        ) {
          error = false;
        }

        if (error) {
          return ${this.makeError({type: "com-uk", actual: "value", messages})};
        }

        return true;
      `,
    };
  }*/,
  );
  v.add(
    "com-be",
    (value: string) => {
      const replacedValue = value.split("BE").join("");
      const firstChar = replacedValue.substring(0, 1);
      let error = true;
      if (!isNaN(replacedValue as any) && (firstChar === "0" || firstChar === "1") && replacedValue.length === 10) {
        error = false;
      }

      if (error) {
        return v.makeError("com-be", null, value);
      }

      return true;
    } /*function (this: Validator, {messages}) {
    return {
      source: `
        const replacedValue = value.split('BE').join('');
        const firstChar = replacedValue.substring(0, 1);
        let error = true;
        if (!isNaN(replacedValue) && (firstChar === '0' || firstChar === '1') && replacedValue.length === 10) {
          error = false;
        }

        if (error) {
          return ${this.makeError({type: "com-be", actual: "value", messages})};
        }

        return true;
      `,
    };
  }*/,
  );

  const emailRx =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  function emailValidatorFunction(value: string) {
    if (value && value.length > 0 && !emailRx.test(value)) {
      return v.makeError("email", null, value);
    }

    if (!rules.optional && (!value || value.length === 0)) {
      return v.makeError("email", null, value);
    }

    return true;
  }
  v.add("graphiteEmail", emailValidatorFunction);
  v.add("email", emailValidatorFunction);
  return v;
}

/**
 * One global instance to use
 */

export function getBaseValidationCheck(validatorInstance, questionData: JSONQuestion) {
  const rules = getBaseValidationRules(questionData);
  if (questionData.requiredForSave) {
    rules.optional = false;
  } else if (!("optional" in rules)) {
    rules.optional = true;
  }
  return getBaseValidationCheckWithoutQuestion(validatorInstance, rules);
}

export function getBaseValidationRules(questionData: JSONQuestion): ValidationRules {
  const rules = pathOr({}, ["validation", "rules"], questionData) as ValidationRules;
  if (questionData.type === QuestionType.EMAIL && !("type" in rules)) {
    //@ts-ignore
    rules.type = ValidationType.GRAPHITE_EMAIL;
  }
  return rules;
}

/**
 * Used when validating a non-question value using same rules
 * @param validatorInstance
 */
export function getBaseValidationCheckWithoutQuestion(validatorInstance, rules: ValidationRules) {
  if (!rules || !rules.type) {
    return () => [];
  }
  if ((rules.type === "number" || rules.type === "boolean" || rules.type === "date") && !("convert" in rules)) {
    rules.convert = true;
  }
  return validatorInstance.compile({value: rules});
}

export function getCompiledAdditionalValidationCheck(
  validatorInstance,
  questionData,
  customValidationCheckOptions: CustomValidationCheckOptions,
) {
  return validatorInstance.compile({
    value: {
      type: "custom",
      check: (value) => {
        return customValidationCheckOptions.validationCheckHandler(questionData, value);
      },
    },
  });
}

/**
 * These are global additional validation checks that are used for any question
 */
const globalAdditionalValidationChecks: CustomValidationCheckOptions[] = [
  {
    validationKey: "requiredForSaveValidation",
    onlyRunIfModified: false,
    skipIfValueIsNull: false,
    validationCheckHandler: (questionData, value: any) => {
      if (advancedIsUnanswered(value, questionData.type)) {
        return [{type: "answerRequiredToSave", expected: null, actual: value}];
      }
      return true;
    },
    addValidationCondition(questionData) {
      return !!questionData.requiredForSave;
    },
  },
];

/**
 * Add an additional validation check to the global array
 *
 * @param additionalCheck
 */
export function addAdditionalValidationCheck(additionalCheck: CustomValidationCheckOptions) {
  globalAdditionalValidationChecks.push(additionalCheck);
}

/**
 * Remove a global validation check by validationKey
 *
 * @param key
 */
export function removeAdditionalValidationCheck(key: string) {
  const index = globalAdditionalValidationChecks.findIndex(prop("validationKey"), key);
  if (index > -1) {
    globalAdditionalValidationChecks.splice(index, 1);
  }
}

/**
 * Gets the validation check, which can be used over and over again
 *
 * @param questionData
 * @param additionalValidationChecks
 */
export function getValidationCheckFromQuestionData(
  questionData: JSONQuestion,
  additionalValidationChecks: CustomValidationCheckOptions[] = [],
) {
  const rules = getBaseValidationRules(questionData);
  const validatorInstance = getValidationInstance(rules);
  let runRules = false;
  const validationChecks: ValidationCheck[] = [];

  // These vars are set by valCheck method returned
  let isModifiedArgument = false;
  let valueIsNotNullOrEmpty = false;

  // Custom, extra checks if inputs want to add their own stuff
  for (const additionalValidationCheck of [...globalAdditionalValidationChecks, ...additionalValidationChecks]) {
    if (
      additionalValidationCheck.addValidationCondition &&
      !additionalValidationCheck.addValidationCondition(questionData)
    ) {
      continue;
    }
    runRules = true;
    const compiledAdditionalCheck = getCompiledAdditionalValidationCheck(
      validatorInstance,
      questionData,
      additionalValidationCheck,
    );
    const skipIfValueIsNull = !isNil(additionalValidationCheck.skipIfValueIsNull)
      ? additionalValidationCheck.skipIfValueIsNull
      : true;
    const onlyRunIfModified = !isNil(additionalValidationCheck.onlyRunIfModified)
      ? additionalValidationCheck.onlyRunIfModified
      : true;
    validationChecks.push((valueObj) => {
      if (
        ((skipIfValueIsNull && isNil(valueObj.value)) || (onlyRunIfModified && !isModifiedArgument)) &&
        !questionData.requiredForSave
      ) {
        return [{makePassesNull: true}];
      }
      const compiledCustomCheckResults = compiledAdditionalCheck(valueObj) as ValidationCheckResults;
      return Array.isArray(compiledCustomCheckResults) ? compiledCustomCheckResults : [];
    });
  }

  // This is using fastest-validators built in and extended types. This should be run last and because we don't want to
  // validate until after the answer has been modified, we need the ability to keep the validation passes value null.
  if (rules) {
    runRules = true;
    const baseValidationCheck = getBaseValidationCheck(validatorInstance, questionData);
    let optionalValidation = pathOr(true, ["validation", "rules", "optional"], questionData);
    validationChecks.push((valueObj) => {
      if ((optionalValidation && valueIsNotNullOrEmpty) || (!optionalValidation && isModifiedArgument)) {
        const baseValidationCheckResults = baseValidationCheck(valueObj) as ValidationCheckResults;
        return Array.isArray(baseValidationCheckResults) ? baseValidationCheckResults : [];
      } else {
        return [{makePassesNull: true}];
      }
    });
  }

  // Returns a method that just required the answer
  return (answer: any, isModified: boolean, validationExpressionError: JSONValue = ""): ValidationSummary => {
    let validationSummary: ValidationSummary = {passes: null, details: {}};
    // sets the values above
    valueIsNotNullOrEmpty = !(isNil(answer) || isEmpty(answer));
    isModifiedArgument = isModified;
    // Run all the compiled rules if we added any
    if (runRules) {
      let validationResponse = [];
      for (const validationCheck of validationChecks) {
        const singleTestResponse = validationCheck({value: answer});
        if (singleTestResponse && singleTestResponse.length) {
          // We only care about the first validation error (which will be an array)
          validationResponse = singleTestResponse;
          if (!pathOr(false, ["0", "makePassesNull"], validationResponse)) {
            break;
          }
        }
      }
      const passesShouldBeNull = Boolean(pathOr(false, ["0", "makePassesNull"], validationResponse));
      if (passesShouldBeNull) {
        validationSummary = {passes: null, details: {}};
      } else {
        validationSummary.passes = !validationResponse.length;
        validationSummary.details = validationResponse.length ? validationResponse[0] : {};
      }
    }

    // For certain questionTypes return Validation Expression regardless if the value is empty/null
    valueIsNotNullOrEmpty = valueIsNotNullOrEmpty ? true : valExpExceptionTypes.includes(questionData?.type);

    // Validation error that comes from compute props (allowing Aaron to set custom validation rules)
    if (valueIsNotNullOrEmpty && validationSummary.passes !== false && validationExpressionError) {
      validationSummary.passes = !validationExpressionError;
      const message = String(
        questionData.type === QuestionType.CONTACT ? (validationExpressionError as any).msg : validationExpressionError,
      );
      validationSummary.details = {message, alreadyTranslated: true};
      if (questionData.type === QuestionType.CONTACT) {
        validationSummary.details.type = (validationExpressionError as any).part;
      }
    }
    // Return
    return validationSummary;
  };
}

export function getExternalValidation(
  questionData: JSONQuestion,
  inputValue: string | object | undefined | null,
  answers: Answers,
): string {
  if (
    !questionData.externalValidation ||
    inputValue === "" ||
    isNil(inputValue) ||
    questionData.hideExternalValidation
  ) {
    return "";
  }
  const group = findGroup(questionData);
  const instance = findGroupInstance(questionData);
  if (group && instance) {
    const keyParts = `${questionData.key}${Internal.VALIDATION_STATUS}`.split(".");
    return pathOr("", keyParts, answers);
  } else {
    return pathOr("", [`${questionData.key}${Internal.VALIDATION_STATUS}`], answers);
  }
}

/**
 * Creates a hash of a group instances answers to compare between instances (used for groupKey validation)
 *
 * @param groupInstance
 * @param instance
 * @param locale
 */
export function getGroupAnswerHash(groupInstance: JSONQuestion, instance: JSONQuestion, locale?: string) {
  const returnObject: {[key: string]: any} = {};
  const groupKeyQuestionDatas = filter(prop("groupKey"), groupInstance).map(
    (questionData: JSONQuestion) => questionData,
  );
  const punctuation = /\W|_/g;

  for (const groupKeyQuestionData of groupKeyQuestionDatas) {
    const questionKey = groupKeyQuestionData.key.split(".")[2];
    const rawAnswer = instance[questionKey];

    returnObject[questionKey] = isLocaleObjectQuestion(groupKeyQuestionData)
      ? getFreeTextAnswer(rawAnswer, locale)
      : rawAnswer;

    if (returnObject[questionKey] && is(String, returnObject[questionKey])) {
      returnObject[questionKey] = returnObject[questionKey].replace(punctuation, "").trim().toLowerCase();
    }
  }
  return JSON.stringify(returnObject);
}

export function strongPasswordValidation() {
  /*
  if (zxcvbnResponse) {
    v.add('strongPassword', function (this: Validator, { messages }, path, context) {
      // @ts-ignore
      context.zxcvbn = zxcvbnResponse.default;  // HACK, consider alternative validation lib
      return {
        source: `
        if (value && value.length >= ${rules.passMin}) {
          const result = context.zxcvbn(value);
          if (result.score >= ${rules.passScore}) {
            return true;
          }
        }
        return ${this.makeError({ type: 'strongPassword',  actual: "value", messages })};
        `
      }
    });
  }
  */
}

export function validationIsAllSuccessful(validationStates: Dictionary<ValidationState>) {
  return !any((k) => {
    return pathOr(null, ["state"], k) === false;
  }, values(validationStates));
}

/**
 * For a lot of questions they might only pay attention to requiredForSave, so why make them use
 * all the validation stuff that is largely only used by text input?
 *
 * @param elementData
 * @param answerValue
 */
export function requiredForSaveValidationCheck(elementData: JSONQuestion, answerValue: any): ValidationSummary {
  if (elementData.requiredForSave && advancedIsUnanswered(answerValue, elementData.type)) {
    return {passes: false, details: {type: "answerRequiredToSave", expected: null, actual: answerValue}};
  }
  return {passes: true, details: {}};
}
